/**
 * Gear Bag Section for Assistant Profile
 * Allows adding and managing gear items with quantity
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, X, Minus } from "lucide-react";
import { GEAR_CATEGORIES } from "@/lib/profileOptions";
import CheckBox from "./CheckBox";

export default function GearBagSection({ gearItems, setGearItems, willingToRent, setWillingToRent }) {
  const [newGearCategory, setNewGearCategory] = useState("camera");
  const [newGearName, setNewGearName] = useState("");
  const [newGearQty, setNewGearQty] = useState(1);

  const addGearItem = () => {
    if (newGearName.trim()) {
      setGearItems([...gearItems, {
        category: newGearCategory,
        name: newGearName.trim(),
        quantity: newGearQty,
        is_backup: false
      }]);
      setNewGearName("");
      setNewGearQty(1);
    }
  };

  const removeGearItem = (index) => {
    setGearItems(gearItems.filter((_, i) => i !== index));
  };

  const toggleGearBackup = (index) => {
    setGearItems(gearItems.map((item, i) => 
      i === index ? { ...item, is_backup: !item.is_backup } : item
    ));
  };

  const updateQuantity = (index, delta) => {
    setGearItems(gearItems.map((item, i) => {
      if (i !== index) return item;
      const newQty = Math.max(1, (item.quantity || 1) + delta);
      return { ...item, quantity: newQty };
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addGearItem();
    }
  };

  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6">
      <h2 className="text-xl font-semibold mb-6">Your Gear Bag</h2>
      
      <div className="space-y-4 mb-6">
        <div className="flex gap-2">
          <select 
            value={newGearCategory} 
            onChange={(e) => setNewGearCategory(e.target.value)}
            className="min-w-[160px] h-12 bg-white/[0.05] border border-white/10 rounded-sm text-white px-3"
            data-testid="gear-category-select"
          >
            {GEAR_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <Input
            value={newGearName}
            onChange={(e) => setNewGearName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g., Canon R5, 70-200mm f/2.8..."
            className="flex-1 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-12 rounded-sm"
            data-testid="gear-name-input"
          />
          <div className="flex items-center gap-1">
            <span className="text-white/40 text-xs mr-1">Qty</span>
            <Input
              type="number"
              min="1"
              value={newGearQty}
              onChange={(e) => setNewGearQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-16 h-12 bg-white/[0.05] border-white/10 text-white text-center rounded-sm"
              data-testid="gear-qty-input"
            />
          </div>
          <Button 
            type="button" 
            onClick={addGearItem} 
            className="bg-white/10 hover:bg-white/20 text-white h-12 px-4 rounded-sm"
            data-testid="add-gear-button"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {gearItems.length > 0 && (
        <div className="space-y-2">
          {GEAR_CATEGORIES.map((cat) => {
            const categoryItems = gearItems.filter(g => g.category === cat.value);
            if (categoryItems.length === 0) return null;
            return (
              <div key={cat.value} className="mb-4">
                <h3 className="text-white/60 text-sm uppercase tracking-wider mb-2">{cat.label}</h3>
                <div className="space-y-2">
                  {categoryItems.map((item) => {
                    const originalIndex = gearItems.indexOf(item);
                    return (
                      <div 
                        key={originalIndex} 
                        className="flex items-center justify-between p-3 bg-white/[0.05] border border-white/10 rounded-sm"
                        data-testid={`gear-item-${originalIndex}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={item.is_backup ? "text-white/60" : ""}>{item.name}</span>
                          {(item.quantity || 1) > 1 && (
                            <span className="text-xs text-white/40 bg-white/10 px-1.5 py-0.5 rounded">x{item.quantity}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => updateQuantity(originalIndex, -1)}
                              className="w-6 h-6 flex items-center justify-center rounded bg-white/5 text-white/40 hover:text-white hover:bg-white/10"
                              data-testid={`gear-qty-minus-${originalIndex}`}
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="text-sm text-white/60 w-6 text-center">{item.quantity || 1}</span>
                            <button
                              type="button"
                              onClick={() => updateQuantity(originalIndex, 1)}
                              className="w-6 h-6 flex items-center justify-center rounded bg-white/5 text-white/40 hover:text-white hover:bg-white/10"
                              data-testid={`gear-qty-plus-${originalIndex}`}
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                          <div 
                            className="flex items-center gap-2 text-sm text-white/50 cursor-pointer"
                            onClick={() => toggleGearBackup(originalIndex)}
                          >
                            <CheckBox checked={item.is_backup} />
                            <span>Backup</span>
                          </div>
                          <button 
                            type="button" 
                            onClick={() => removeGearItem(originalIndex)} 
                            className="text-white/40 hover:text-red-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Willing to rent gear checkbox */}
      <div
        className="flex items-center gap-3 mt-6 pt-4 border-t border-white/10 cursor-pointer"
        onClick={() => setWillingToRent && setWillingToRent(!willingToRent)}
        data-testid="willing-to-rent-toggle"
      >
        <CheckBox checked={!!willingToRent} />
        <div>
          <span className="text-sm font-medium">Willing to rent gear</span>
          <p className="text-xs text-white/40">I'm open to renting specific gear if required for a job</p>
        </div>
      </div>
    </Card>
  );
}
